import { Confirmation } from 'primeng/api';
import {
  ActionDto,
  ActionReferenceDto,
  ConditionalGroupDto,
  DependencySourceEnum,
  RemovedDependency,
  SegmentDto,
  StepDto,
} from 'workflow-definition/openapi';

// stepRefId can arrive as a string at runtime (e.g. '55'), so compare numerically.
function groupsReferenceStep(
  groups: ConditionalGroupDto[] | null | undefined,
  stepId: number
): boolean {
  return (
    groups?.some(group =>
      group.rules?.some(
        rule =>
          Number(rule.val1?.stepRefId) === stepId ||
          Number(rule.val2?.stepRefId) === stepId
      )
    ) ?? false
  );
}

export function findStepsReferencing(
  steps: StepDto[] | null | undefined,
  movedStepId: number
): string[] {
  const names: string[] = [];
  (steps ?? []).forEach((step, index) => {
    if (step.id === movedStepId) return;
    const referencesMovedStep =
      groupsReferenceStep(step.conditionals, movedStepId) ||
      groupsReferenceStep(step.blockers, movedStepId) ||
      (step.actions ?? []).some(
        action =>
          groupsReferenceStep(action.conditionals, movedStepId) ||
          groupsReferenceStep(action.blockers, movedStepId)
      );
    if (referencesMovedStep) names.push(step.name || `Step ${index + 1}`);
  });
  return names;
}

export interface InvalidatedDependency {
  ownerName: string;
  referencedName: string;
  source: DependencySourceEnum;
}

const SOURCE_LABELS: Record<DependencySourceEnum, string> = {
  Condition: 'condition',
  Blocker: 'blocker',
  PrePopulate: 'pre-populated value',
  Label: 'label text',
  ScanParams: 'scan parameters',
  SendList: 'actions to send',
  Trigger: 'trigger',
};

export function describeDependency(
  ownerName: string,
  source: DependencySourceEnum | undefined,
  referencedName: string
): string {
  const kind = source ? SOURCE_LABELS[source] : 'dependency';
  return `${ownerName}: ${kind} on ${referencedName}`;
}

export function describeInvalidatedDependency(
  dependency: InvalidatedDependency
): string {
  return describeDependency(
    dependency.ownerName,
    dependency.source,
    dependency.referencedName
  );
}

export function describeRemovedDependency(removed: RemovedDependency): string {
  const owner =
    [removed.ownerStepName, removed.ownerActionName]
      .filter(Boolean)
      .join(' › ') ||
    removed.ownerSegmentName ||
    'An item';
  return describeDependency(
    owner,
    removed.source,
    removed.referencedName || 'a later item'
  );
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildReorderConfirmation(
  invalidated: InvalidatedDependency[],
  onAccept: () => void,
  onReject: () => void
): Confirmation {
  const list = invalidated
    .map(item => `<li>${escapeHtml(describeInvalidatedDependency(item))}</li>`)
    .join('');
  return {
    header: 'Clear dependencies?',
    message:
      'This new order makes these dependencies invalid, so their references will be cleared and need to be selected again:' +
      `<ul>${list}</ul>`,
    acceptLabel: 'Reorder anyway',
    rejectLabel: 'Cancel',
    acceptButtonStyleClass: 'wf-confirm-accept',
    rejectButtonStyleClass: 'wf-confirm-reject',
    accept: onAccept,
    reject: onReject,
  };
}

interface Targets {
  segments: Map<number, string>;
  steps: Map<number, string>;
  actions: Map<number, string>;
}

interface Ordered {
  id?: number;
  order?: number | null;
}

function toId(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
}

// Siblings the moved item passes over, mirroring the backend: moving up they end up after
// it, moving down they end up before it. The earlier of each flipped pair is the one that
// must not reference the later.
function splitByMove<T extends Ordered>(
  items: T[],
  movedId: number,
  targetOrder: number
): { moved: T; jumped: T[]; movedUp: boolean } | null {
  const orderOf = (item: T, index: number) => item.order ?? index;
  const movedIndex = items.findIndex(item => Number(item.id) === movedId);
  if (movedIndex < 0) return null;

  const moved = items[movedIndex];
  const movedOrder = orderOf(moved, movedIndex);
  if (targetOrder === movedOrder) return null;

  const movedUp = targetOrder < movedOrder;
  const jumped = items.filter((item, index) => {
    if (index === movedIndex) return false;
    const order = orderOf(item, index);
    return movedUp
      ? order >= targetOrder && order < movedOrder
      : order > movedOrder && order <= targetOrder;
  });
  return { moved, jumped, movedUp };
}

function addStepTargets(
  targets: Targets,
  steps: StepDto[] | null | undefined
): void {
  (steps ?? []).forEach((step, index) => {
    const stepId = toId(step.id);
    if (stepId !== null)
      targets.steps.set(stepId, step.name || `Step ${index + 1}`);
    (step.actions ?? []).forEach(action => {
      const actionId = toId(action.id);
      if (actionId !== null)
        targets.actions.set(actionId, action.name || 'Action');
    });
  });
}

function matchesValue(
  value:
    | { segmentRefId?: unknown; stepRefId?: unknown; actionRefId?: unknown }
    | null
    | undefined,
  targets: Targets
): string | null {
  if (!value) return null;
  const segmentId = toId(value.segmentRefId);
  if (segmentId !== null && targets.segments.has(segmentId))
    return targets.segments.get(segmentId) ?? null;
  const stepId = toId(value.stepRefId);
  if (stepId !== null && targets.steps.has(stepId))
    return targets.steps.get(stepId) ?? null;
  const actionId = toId(value.actionRefId);
  if (actionId !== null && targets.actions.has(actionId))
    return targets.actions.get(actionId) ?? null;
  return null;
}

function matchesReference(
  reference: ActionReferenceDto,
  targets: Targets
): string | null {
  const refId = toId(reference.refId);
  if (refId === null) return null;
  const pool =
    reference.kind === 'Segment'
      ? targets.segments
      : reference.kind === 'Step'
        ? targets.steps
        : targets.actions;
  return pool.get(refId) ?? null;
}

function collectFromGroups(
  groups: ConditionalGroupDto[] | null | undefined,
  source: DependencySourceEnum,
  ownerName: string,
  targets: Targets,
  found: InvalidatedDependency[]
): void {
  (groups ?? []).forEach(group =>
    (group.rules ?? []).forEach(rule => {
      [rule.val1, rule.val2].forEach(value => {
        const referencedName = matchesValue(value, targets);
        if (referencedName) found.push({ ownerName, referencedName, source });
      });
    })
  );
}

function collectFromAction(
  action: ActionDto,
  ownerName: string,
  targets: Targets,
  found: InvalidatedDependency[]
): void {
  collectFromGroups(
    action.conditionals,
    'Condition',
    ownerName,
    targets,
    found
  );
  collectFromGroups(action.blockers, 'Blocker', ownerName, targets, found);
  (action.references ?? []).forEach(reference => {
    const referencedName = matchesReference(reference, targets);
    if (referencedName && reference.source)
      found.push({ ownerName, referencedName, source: reference.source });
  });
}

function collectFromStep(
  step: StepDto,
  index: number,
  targets: Targets,
  found: InvalidatedDependency[]
): void {
  const stepName = step.name || `Step ${index + 1}`;
  collectFromGroups(step.conditionals, 'Condition', stepName, targets, found);
  collectFromGroups(step.blockers, 'Blocker', stepName, targets, found);
  (step.actions ?? []).forEach(action =>
    collectFromAction(
      action,
      `${stepName} › ${action.name || 'Action'}`,
      targets,
      found
    )
  );
}

function unique(found: InvalidatedDependency[]): InvalidatedDependency[] {
  const seen = new Set<string>();
  return found.filter(item => {
    const key = `${item.ownerName}|${item.source}|${item.referencedName}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Dependencies that moving a step to `targetOrder` would make invalid, so the Studio can warn
 * before saving. Mirrors the backend rule: for every sibling whose order relative to the moved
 * step flips, the step that is now earlier must not reference the one that is now later.
 */
export function findStepDependenciesInvalidatedByMove(
  steps: StepDto[] | null | undefined,
  movedStepId: number,
  targetOrder: number
): InvalidatedDependency[] {
  const list = steps ?? [];
  const split = splitByMove(list, movedStepId, targetOrder);
  if (!split) return [];

  const owners = split.movedUp ? [split.moved] : split.jumped;
  const targetSteps = split.movedUp ? split.jumped : [split.moved];
  const targets: Targets = {
    segments: new Map(),
    steps: new Map(),
    actions: new Map(),
  };
  addStepTargets(targets, targetSteps);

  const found: InvalidatedDependency[] = [];
  owners.forEach(owner =>
    collectFromStep(owner, list.indexOf(owner), targets, found)
  );
  return unique(found);
}

/** Same rule one level up: segments passed over by the moved segment, with their steps and actions. */
export function findSegmentDependenciesInvalidatedByMove(
  segments: SegmentDto[] | null | undefined,
  movedSegmentId: number,
  targetOrder: number
): InvalidatedDependency[] {
  const list = segments ?? [];
  const split = splitByMove(list, movedSegmentId, targetOrder);
  if (!split) return [];

  const owners = split.movedUp ? [split.moved] : split.jumped;
  const targetSegments = split.movedUp ? split.jumped : [split.moved];
  const targets: Targets = {
    segments: new Map(),
    steps: new Map(),
    actions: new Map(),
  };
  targetSegments.forEach((segment, index) => {
    const segmentId = toId(segment.id);
    if (segmentId !== null)
      targets.segments.set(segmentId, segment.name || `Segment ${index + 1}`);
    addStepTargets(targets, segment.steps);
  });

  const found: InvalidatedDependency[] = [];
  owners.forEach(owner => {
    const segmentName = owner.name || `Segment ${list.indexOf(owner) + 1}`;
    collectFromGroups(
      owner.conditionals,
      'Condition',
      segmentName,
      targets,
      found
    );
    collectFromGroups(owner.blockers, 'Blocker', segmentName, targets, found);
    (owner.steps ?? []).forEach((step, index) =>
      collectFromStep(step, index, targets, found)
    );
  });
  return unique(found);
}
